/**
 * Product Roadmap Editor
 *
 * Two view modes:
 *   Horizon      — Now / Next / Later Kanban (3 columns)
 *   Uncertainties — 3×3 matrix of horizon × confidence
 *
 * The Uncertainties view encodes a core distinction: a roadmap
 * is not a plan of record. Showing confidence explicitly forces
 * teams to separate what is committed from what is directional
 * intent or a hypothesis still needing discovery.
 *
 * High confidence / Now ≈ plan-of-record territory.
 * Uncertain / Later     = hypothesis. Do discovery first.
 *
 * Route: /prioritization/product-roadmap/:id
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Loader2,
  AlertCircle,
  MoreHorizontal,
  Trash2,
  MoveRight,
  Edit3,
  Link2,
  CheckCircle2,
  Circle,
  PlayCircle,
  XCircle,
  ChevronDown,
  X,
  Tag,
  LayoutList,
  Grid3x3,
  Info,
  AlertTriangle,
  Table2,
} from 'lucide-react';
import {
  useProductRoadmapStore,
  type ProductRoadmapItem,
  type ProductRoadmapHorizon,
  type ProductRoadmapConfidence,
  type ProductRoadmapItemStatus,
  type ProductRoadmapDocumentStatus,
} from '../../stores/prioritization/productRoadmapStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { SearchableItemPicker } from '../../components/prioritization/SearchableItemPicker';
import { useVisualViewportFlags } from '../../hooks/prioritization/useVisualViewport';

// ── View mode ──────────────────────────────────────────────

type ViewMode = 'horizon' | 'uncertainties' | 'list';

// ── Horizon config ─────────────────────────────────────────

interface HorizonConfig {
  id: ProductRoadmapHorizon;
  label: string;
  description: string;
  accentClass: string;
  headerBg: string;
  badgeBg: string;
  addBtnClass: string;
  borderClass: string;
  columnBg: string;
}

const HORIZONS: HorizonConfig[] = [
  {
    id: 'now',
    label: 'Now',
    description: 'Committed and in active execution',
    accentClass: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    headerBg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border-[var(--green)] dark:border-[var(--green)]/50',
    badgeBg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]',
    addBtnClass: 'text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] dark:hover:bg-[var(--green)]/20',
    borderClass: 'border-[var(--green)] dark:border-[var(--green)]/40',
    columnBg: 'bg-[var(--green-dim)]/30 dark:bg-[var(--green)]/20',
  },
  {
    id: 'next',
    label: 'Next',
    description: 'Prioritized, starting soon',
    accentClass: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
    headerBg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-[var(--mustard)] dark:border-[var(--mustard)]/50',
    badgeBg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 text-[var(--mustard)] dark:text-[var(--mustard)]',
    addBtnClass: 'text-[var(--mustard)] dark:text-[var(--mustard)] hover:bg-[var(--mustard-dim)] dark:hover:bg-[var(--mustard)]/20',
    borderClass: 'border-[var(--mustard)] dark:border-[var(--mustard)]/40',
    columnBg: 'bg-[var(--mustard-dim)]/30 dark:bg-[var(--mustard)]/20',
  },
  {
    id: 'later',
    label: 'Later',
    description: 'Important, but further out in the sequence',
    accentClass: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
    headerBg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border-[var(--slate)] dark:border-[var(--slate)]/50',
    badgeBg: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)]',
    addBtnClass: 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20',
    borderClass: 'border-[var(--slate)] dark:border-[var(--slate)]/40',
    columnBg: 'bg-[var(--slate-dim)]/30 dark:bg-[var(--slate)]/20',
  },
];

// ── Confidence config ──────────────────────────────────────

interface ConfidenceConfig {
  id: ProductRoadmapConfidence;
  label: string;
  sublabel: string;
  description: string;
  rowBg: string;
  headerBg: string;
  accentClass: string;
  badgeBg: string;
  borderStyle: string;
  warningHorizons?: ProductRoadmapHorizon[];
}

const CONFIDENCE_LEVELS: ConfidenceConfig[] = [
  {
    id: 'high',
    label: 'High Confidence',
    sublabel: 'Well understood — ready to build',
    description: 'We understand the problem and the solution well enough to go straight to development. Low discovery risk.',
    rowBg: 'bg-[var(--green-dim)]/40 dark:bg-[var(--green)]/20',
    headerBg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/25 border-[var(--green)] dark:border-[var(--green)]/50',
    accentClass: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    badgeBg: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]',
    borderStyle: 'border-[var(--green)] dark:border-[var(--green)]/40',
  },
  {
    id: 'medium',
    label: 'Medium Confidence',
    sublabel: 'Partially understood — still being defined',
    description: 'We know the direction but the problem or solution still has open questions. Some definition or light exploration is needed before full development.',
    rowBg: 'bg-[var(--mustard-dim)]/30 dark:bg-[var(--mustard)]/15',
    headerBg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-[var(--mustard)] dark:border-[var(--mustard)]/50',
    accentClass: 'text-[var(--mustard)] dark:text-[var(--mustard)]',
    badgeBg: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 text-[var(--mustard)] dark:text-[var(--mustard)]',
    borderStyle: 'border-[var(--mustard)] dark:border-[var(--mustard)]/40',
  },
  {
    id: 'uncertain',
    label: 'Uncertain',
    sublabel: 'Not yet understood — experiment first',
    description: 'We know this area matters but we don\'t yet understand the problem or solution well enough to build. The path to market runs through experimentation, not development.',
    rowBg: 'bg-[var(--surface)]/40 dark:bg-[var(--bg)]/20',
    headerBg: 'bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)]/40 border-[var(--rule)] dark:border-[var(--rule)]/50',
    accentClass: 'text-[var(--text-sec)]',
    badgeBg: 'bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)]/40 text-[var(--text-sec)]',
    borderStyle: 'border-dashed border-[var(--rule)] dark:border-[var(--rule)]/50',
  },
];

// ── Status config ──────────────────────────────────────────

const STATUS_CONFIG: Record<ProductRoadmapItemStatus, { label: string; icon: React.ReactNode; cls: string }> = {
  planned: {
    label: 'Planned',
    icon: <Circle className="h-3 w-3" />,
    cls: 'text-[var(--text-ter)]',
  },
  in_progress: {
    label: 'In progress',
    icon: <PlayCircle className="h-3 w-3" />,
    cls: 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
  },
  done: {
    label: 'Done',
    icon: <CheckCircle2 className="h-3 w-3" />,
    cls: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  },
  dropped: {
    label: 'Dropped',
    icon: <XCircle className="h-3 w-3" />,
    cls: 'text-[var(--coral)] dark:text-[var(--coral)]',
  },
};

// ── Document status config ─────────────────────────────────

const DOC_STATUS_CONFIG: Record<
  ProductRoadmapDocumentStatus,
  { label: string; dotCls: string; badgeCls: string }
> = {
  draft: { label: 'Draft', dotCls: 'bg-[var(--surface)]', badgeCls: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  active: { label: 'Active', dotCls: 'bg-[var(--green)]', badgeCls: 'bg-[var(--green-dim)] dark:bg-[var(--green)]/25 text-[var(--green)] dark:text-[var(--text-ter)]' },
  on_hold: { label: 'On Hold', dotCls: 'bg-[var(--mustard)]', badgeCls: 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/25 text-[var(--mustard)] dark:text-[var(--mustard)]' },
  completed: { label: 'Completed', dotCls: 'bg-[var(--slate)]', badgeCls: 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/25 text-[var(--text-sec)]' },
  archived: { label: 'Archived', dotCls: 'bg-[var(--rule-bright)]', badgeCls: 'bg-[var(--hover-tint)] text-[var(--text-ter)]' },
};

const DOC_STATUS_ORDER: ProductRoadmapDocumentStatus[] = ['draft', 'active', 'on_hold', 'completed', 'archived'];

function DocStatusBadge({
  status,
  onChange,
}: {
  status: ProductRoadmapDocumentStatus;
  onChange: (s: ProductRoadmapDocumentStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cfg = DOC_STATUS_CONFIG[status];

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity ${cfg.badgeCls}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
        {cfg.label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-36 bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] shadow-lg py-1">
          {DOC_STATUS_ORDER.map((s) => {
            const c = DOC_STATUS_CONFIG[s];
            return (
              <button
                key={s}
                type="button"
                onClick={() => { onChange(s); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                <span className={`w-1.5 h-1.5 rounded-full ${c.dotCls}`} />
                <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{c.label}</span>
                {s === status && <span className="ml-auto text-[var(--text-ter)]">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Item-level inline status picker ────────────────────────

const ITEM_STATUS_ORDER: ProductRoadmapItemStatus[] = ['planned', 'in_progress', 'done', 'dropped'];

function ItemStatusBadge({
  status,
  onChange,
}: {
  status: ProductRoadmapItemStatus;
  onChange: (s: ProductRoadmapItemStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const cfg = STATUS_CONFIG[status];

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium hover:opacity-80 transition-opacity bg-[var(--surface)] border border-[var(--rule)] ${cfg.cls}`}
      >
        {cfg.icon}
        {cfg.label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-36 bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] shadow-lg py-1">
          {ITEM_STATUS_ORDER.map((s) => {
            const c = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(s); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                <span className={c.cls}>{c.icon}</span>
                <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{c.label}</span>
                {s === status && <span className="ml-auto text-[var(--text-ter)]">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Confidence badge ───────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence: ProductRoadmapConfidence }) {
  const cfg = CONFIDENCE_LEVELS.find((c) => c.id === confidence)!;
  return (
    <span className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded ${cfg.badgeBg}`}>
      {cfg.label}
    </span>
  );
}

// ── Item card ──────────────────────────────────────────────

interface ItemCardProps {
  item: ProductRoadmapItem;
  horizons: HorizonConfig[];
  confidenceLevels: ConfidenceConfig[];
  showConfidence?: boolean;
  showHorizon?: boolean;
  warnUncertainNow?: boolean;
  onEdit: (item: ProductRoadmapItem) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, horizon: ProductRoadmapHorizon) => void;
  linkedItemLabel: string | null;
}

function ItemCard({
  item,
  horizons,
  showConfidence = false,
  showHorizon = false,
  warnUncertainNow = false,
  onEdit,
  onDelete,
  onMove,
  linkedItemLabel,
}: ItemCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div className={`group relative bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border  p-3 hover:shadow-sm transition-all ${
      warnUncertainNow
        ? 'border-[var(--mustard)] dark:border-[var(--mustard)]/60'
        : 'border-[var(--rule)] hover:border-[var(--rule)] dark:hover:border-[var(--rule)]'
    }`}>
      {warnUncertainNow && (
        <div className="flex items-center gap-1.5 text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mb-2 pb-2 border-b border-[var(--mustard)] dark:border-[var(--mustard)]/40">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          Active discovery — this is being explored now, not built
        </div>
      )}

      <div className="flex items-start justify-between gap-2">
        <p
          className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug cursor-pointer hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] flex-1"
          onClick={() => onEdit(item)}
        >
          {item.title || <span className="italic text-[var(--text-ter)]">Untitled</span>}
        </p>
        <div ref={menuRef} className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-all"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-6 z-20 w-44 bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] border border-[var(--rule)] shadow-lg py-1 text-sm">
              <button
                onClick={() => { onEdit(item); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                <Edit3 className="h-3.5 w-3.5" /> Edit
              </button>
              <div className="border-t border-[var(--rule)] my-1" />
              <p className="px-3 py-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wider">
                Move to
              </p>
              {horizons
                .filter((h) => h.id !== item.horizon)
                .map((h) => (
                  <button
                    key={h.id}
                    onClick={() => { onMove(item.id, h.id); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
                  >
                    <MoveRight className="h-3.5 w-3.5" />
                    {h.label}
                  </button>
                ))}
              <div className="border-t border-[var(--rule)] my-1" />
              <button
                onClick={() => { onDelete(item.id); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[var(--coral)] dark:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {item.description && (
        <p className="mt-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed line-clamp-2">
          {item.description}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {showHorizon && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)]">
            {HORIZONS.find((h) => h.id === item.horizon)?.label}
          </span>
        )}

        {showConfidence && <ConfidenceBadge confidence={item.confidence} />}

        {item.tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-sec)]"
          >
            <Tag className="h-2.5 w-2.5" />
            {tag}
          </span>
        ))}

        {linkedItemLabel && (
          <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]/40 ml-auto">
            <Link2 className="h-2.5 w-2.5" />
            {linkedItemLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Horizon view (3-column Kanban) ─────────────────────────

interface HorizonColumnProps {
  config: HorizonConfig;
  items: ProductRoadmapItem[];
  allHorizons: HorizonConfig[];
  linkedItemLabels: Record<string, string | null>;
  onAdd: (horizon: ProductRoadmapHorizon) => void;
  onEdit: (item: ProductRoadmapItem) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, horizon: ProductRoadmapHorizon) => void;
  onDragOver: (e: React.DragEvent, horizon: ProductRoadmapHorizon) => void;
  onDrop: (e: React.DragEvent, horizon: ProductRoadmapHorizon) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  isDragTarget: boolean;
}

function HorizonColumn({
  config, items, allHorizons, linkedItemLabels,
  onAdd, onEdit, onDelete, onMove,
  onDragOver, onDrop, onDragStart, isDragTarget,
}: HorizonColumnProps) {
  return (
    <div
      className={`flex flex-col  border ${config.borderClass} ${isDragTarget ? 'ring-2 ring-blue-400 dark:ring-blue-500' : ''} transition-all`}
      onDragOver={(e) => onDragOver(e, config.id)}
      onDrop={(e) => onDrop(e, config.id)}
    >
      <div className={`flex items-center justify-between px-4 py-3  border-b ${config.headerBg}`}>
        <div>
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-bold ${config.accentClass}`}>{config.label}</h3>
            <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${config.badgeBg}`}>
              {items.length}
            </span>
          </div>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{config.description}</p>
        </div>
        <button
          onClick={() => onAdd(config.id)}
          className={`p-1.5  ${config.addBtnClass} transition-colors`}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 p-3 space-y-2.5 min-h-[200px] bg-[var(--surface)]/50 dark:bg-[var(--bg)]/20">
        {items.map((item) => (
          <div key={item.id} draggable onDragStart={(e) => onDragStart(e, item.id)} className="cursor-grab active:cursor-grabbing">
            <ItemCard
              item={item}
              horizons={allHorizons}
              confidenceLevels={CONFIDENCE_LEVELS}
              showConfidence
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              linkedItemLabel={linkedItemLabels[item.id] ?? null}
            />
          </div>
        ))}
        {items.length === 0 && (
          <button
            onClick={() => onAdd(config.id)}
            className={`w-full py-6 border-2 border-dashed border-[var(--rule)]  text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:border-[var(--rule)] dark:hover:border-[var(--rule)] ${config.addBtnClass} transition-colors`}
          >
            <Plus className="h-4 w-4 mx-auto mb-1 opacity-60" />
            Add first item
          </button>
        )}
      </div>
    </div>
  );
}

// ── Uncertainties view (3×3 matrix) ───────────────────────

interface UncertaintiesCellProps {
  horizon: HorizonConfig;
  confidence: ConfidenceConfig;
  items: ProductRoadmapItem[];
  allHorizons: HorizonConfig[];
  linkedItemLabels: Record<string, string | null>;
  onAdd: (horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => void;
  onEdit: (item: ProductRoadmapItem) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, horizon: ProductRoadmapHorizon) => void;
  onDragOver: (e: React.DragEvent, horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => void;
  onDrop: (e: React.DragEvent, horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  isDragTarget: boolean;
}

function UncertaintiesCell({
  horizon, confidence, items, allHorizons, linkedItemLabels,
  onAdd, onEdit, onDelete, onMove,
  onDragOver, onDrop, onDragStart, isDragTarget,
}: UncertaintiesCellProps) {
  const warnUncertain = confidence.id === 'uncertain' && horizon.id === 'now';

  return (
    <div
      className={`min-h-[120px] p-2  border ${confidence.borderStyle} ${
        isDragTarget ? 'ring-2 ring-blue-400 dark:ring-blue-500 bg-[var(--slate-dim)]/20 dark:bg-[var(--slate)]/10' : ''
      } transition-all relative group/cell`}
      onDragOver={(e) => onDragOver(e, horizon.id, confidence.id)}
      onDrop={(e) => onDrop(e, horizon.id, confidence.id)}
    >
      {warnUncertain && items.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mb-1.5">
          <AlertTriangle className="h-3 w-3" />
          Active discovery — plan experiments, not delivery
        </div>
      )}

      <div className="space-y-1.5">
        {items.map((item) => (
          <div key={item.id} draggable onDragStart={(e) => onDragStart(e, item.id)} className="cursor-grab active:cursor-grabbing">
            <ItemCard
              item={item}
              horizons={allHorizons}
              confidenceLevels={CONFIDENCE_LEVELS}
              showHorizon={false}
              showConfidence={false}
              warnUncertainNow={warnUncertain}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              linkedItemLabel={linkedItemLabels[item.id] ?? null}
            />
          </div>
        ))}
      </div>

      <button
        onClick={() => onAdd(horizon.id, confidence.id)}
        className="mt-1.5 w-full opacity-0 group-hover/cell:opacity-100 transition-opacity flex items-center justify-center gap-1 py-1.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] border border-dashed border-[var(--rule)] rounded"
      >
        <Plus className="h-3 w-3" /> Add
      </button>
    </div>
  );
}

function UncertaintiesView({
  roadmapId,
  allItems,
  linkedItemLabels,
  onAdd,
  onEdit,
  onDelete,
  onMove,
  onDragStart,
  dragTarget,
  onDragOver,
  onDrop,
}: {
  roadmapId: string;
  allItems: ProductRoadmapItem[];
  linkedItemLabels: Record<string, string | null>;
  onAdd: (horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => void;
  onEdit: (item: ProductRoadmapItem) => void;
  onDelete: (id: string) => void;
  onMove: (id: string, horizon: ProductRoadmapHorizon) => void;
  onDragStart: (e: React.DragEvent, id: string) => void;
  dragTarget: { horizon: ProductRoadmapHorizon; confidence: ProductRoadmapConfidence } | null;
  onDragOver: (e: React.DragEvent, horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => void;
  onDrop: (e: React.DragEvent, horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => void;
}) {
  const { isPhone } = useVisualViewportFlags();
  const [expandedConfidence, setExpandedConfidence] = useState<ProductRoadmapConfidence | null>('high');

  const getItems = (h: ProductRoadmapHorizon, c: ProductRoadmapConfidence) =>
    allItems.filter((i) => i.roadmapId === roadmapId && i.horizon === h && i.confidence === c)
      .sort((a, b) => a.position - b.position);

  const toggleConfidence = (confId: ProductRoadmapConfidence) => {
    setExpandedConfidence((prev) => (prev === confId ? null : confId));
  };

  const renderHorizonCells = (conf: ConfidenceConfig) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      {HORIZONS.map((horiz) => (
        <UncertaintiesCell
          key={horiz.id}
          horizon={horiz}
          confidence={conf}
          items={getItems(horiz.id, conf.id)}
          allHorizons={HORIZONS}
          linkedItemLabels={linkedItemLabels}
          onAdd={onAdd}
          onEdit={onEdit}
          onDelete={onDelete}
          onMove={onMove}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onDragStart={onDragStart}
          isDragTarget={
            dragTarget?.horizon === horiz.id && dragTarget?.confidence === conf.id
          }
        />
      ))}
    </div>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Explanatory banner */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-[var(--surface)] dark:bg-[var(--bg)]/40 border border-[var(--rule)] dark:border-[var(--rule)]/50 text-sm text-[var(--text-sec)]">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-[var(--text-ter)]" />
        <div>
          <span className="font-medium">Certainty is about how well you understand what you're building — not whether you've committed to it.</span>
          {' '}High confidence items are well understood: go build them. Uncertain items are known to matter but not yet understood: run experiments first, then build. This is a risk signal and a strategy signal. It tells you — and your leaders — which items need exploration before they can have a delivery plan.
        </div>
      </div>

      {/* Grid */}
      {isPhone ? (
        <div className="border border-[var(--rule)] overflow-hidden divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {CONFIDENCE_LEVELS.map((conf) => {
            const rowItemCount = HORIZONS.reduce(
              (sum, h) => sum + getItems(h.id, conf.id).length,
              0,
            );
            const isOpen = expandedConfidence === conf.id;

            return (
              <div key={conf.id}>
                <button
                  type="button"
                  onClick={() => toggleConfidence(conf.id)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left border-l-4 ${conf.borderStyle} ${conf.headerBg}`}
                >
                  <div className="min-w-0 text-left">
                    <p className={`text-sm font-semibold ${conf.accentClass}`}>{conf.label}</p>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 italic truncate">{conf.sublabel}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      {rowItemCount} item{rowItemCount !== 1 ? 's' : ''}
                    </span>
                    <ChevronDown className={`h-4 w-4 text-[var(--text-ter)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className={`px-3 py-3 space-y-3 ${conf.rowBg}`}>
                    {renderHorizonCells(conf)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          {/* Column headers */}
          <div className="grid grid-cols-[180px_1fr_1fr_1fr] gap-2 mb-2 sticky top-0 z-20 bg-[var(--bg-raised)] dark:bg-[var(--surface)] pb-2">
            <div className="sticky left-0 z-30 bg-[var(--bg-raised)] dark:bg-[var(--surface)]" />
            {HORIZONS.map((h) => (
              <div key={h.id} className={`px-4 py-2.5  border text-center ${h.headerBg}`}>
                <p className={`text-sm font-bold ${h.accentClass}`}>{h.label}</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{h.description}</p>
              </div>
            ))}
          </div>

          {/* Rows */}
          {CONFIDENCE_LEVELS.map((conf) => (
            <div key={conf.id} className={`grid grid-cols-[180px_1fr_1fr_1fr] gap-2 mb-2  p-2 ${conf.rowBg}`}>
              {/* Row header */}
              <div className={`sticky left-0 z-10 flex flex-col justify-start px-3 py-3  border ${conf.headerBg} ${conf.borderStyle} shadow-[2px_0_4px_-2px_rgba(0,0,0,0.06)]`}>
                <p className={`text-sm font-semibold ${conf.accentClass}`}>{conf.label}</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 italic">{conf.sublabel}</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-2 leading-relaxed hidden lg:block">
                  {conf.description}
                </p>
              </div>

              {/* Cells */}
              {HORIZONS.map((horiz) => (
                <UncertaintiesCell
                  key={horiz.id}
                  horizon={horiz}
                  confidence={conf}
                  items={getItems(horiz.id, conf.id)}
                  allHorizons={HORIZONS}
                  linkedItemLabels={linkedItemLabels}
                  onAdd={onAdd}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onMove={onMove}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragStart={onDragStart}
                  isDragTarget={
                    dragTarget?.horizon === horiz.id && dragTarget?.confidence === conf.id
                  }
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

// ── Item edit modal ────────────────────────────────────────

interface EditModalProps {
  item: Partial<ProductRoadmapItem> & { horizon: ProductRoadmapHorizon };
  mode: 'create' | 'edit';
  onClose: () => void;
  onSave: (updates: Partial<ProductRoadmapItem>) => Promise<void>;
  defaultConfidence?: ProductRoadmapConfidence;
}

function ItemEditModal({ item, mode, onClose, onSave, defaultConfidence }: EditModalProps) {
  const workspaceItems = useItemsStore((s) => s.items);
  const [title, setTitle] = useState(item.title ?? '');
  const [description, setDescription] = useState(item.description ?? '');
  // Status is preserved from the existing item (or defaults to 'planned') and only editable from the List view
  const [status] = useState<ProductRoadmapItemStatus>(item.status ?? 'planned');
  const [horizon, setHorizon] = useState<ProductRoadmapHorizon>(item.horizon);
  const [confidence, setConfidence] = useState<ProductRoadmapConfidence>(item.confidence ?? defaultConfidence ?? 'high');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(item.tags ?? []);
  const [itemId, setItemId] = useState<string | null>(item.itemId ?? null);
  const [saving, setSaving] = useState(false);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput('');
  };

  const warnUncertainNow = confidence === 'uncertain' && horizon === 'now';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    await onSave({ title: title.trim(), description: description.trim() || null, status, horizon, confidence, tags, itemId });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {mode === 'create' ? 'Add item' : 'Edit item'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {warnUncertainNow && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]/40 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>This item is uncertain <strong>and</strong> in Now — meaning you're actively running discovery on it. Make sure the team is planning experiments, not a delivery sprint.</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Title</label>
            <input
              type="text"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What are we building?"
              className="w-full px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Link to workspace item — uses shared SearchableItemPicker for consistent UX */}
          <SearchableItemPicker
            label="Link to workspace item"
            value={itemId ?? undefined}
            onChange={(id) => {
              setItemId(id ?? null);
              if (id && !title.trim()) {
                const linked = workspaceItems.find((w) => w.id === id);
                if (linked?.title) setTitle(linked.title);
              }
            }}
            helperText="Connects this card to a workspace item for Back Plans and the dependency graph."
            placeholder="Search workspace items..."
            emptyLabel="No linked item"
            allowEmpty
          />

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
              Description <span className="text-[var(--text-ter)]">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={confidence === 'uncertain' ? 'What do we need to learn? What experiment would increase our understanding?' : 'Context, outcome, or scope...'}
              rows={3}
              className="w-full px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Horizon + Confidence row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Horizon</label>
              <div className="relative">
                <select
                  value={horizon}
                  onChange={(e) => setHorizon(e.target.value as ProductRoadmapHorizon)}
                  className="w-full appearance-none px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="now">Now</option>
                  <option value="next">Next</option>
                  <option value="later">Later</option>
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Confidence</label>
              <div className="relative">
                <select
                  value={confidence}
                  onChange={(e) => setConfidence(e.target.value as ProductRoadmapConfidence)}
                  className="w-full appearance-none px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="high">High — well understood, ready to build</option>
                  <option value="medium">Medium — partially understood, still being defined</option>
                  <option value="uncertain">Uncertain — not yet understood, experiment first</option>
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">Tags</label>
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)]">
                  {t}
                  <button type="button" onClick={() => setTags((prev) => prev.filter((x) => x !== t))} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]">
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add tag, press Enter"
                className="flex-1 px-3 py-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button type="button" onClick={addTag} disabled={!tagInput.trim()} className="px-3 py-1.5 text-sm bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] disabled:opacity-40 transition-colors">
                Add
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-[var(--rule)]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || saving}
              className="px-4 py-2 text-sm bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {mode === 'create' ? 'Add' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Roadmap list view (table of all cards) ─────────────────

const HORIZON_ORDER: Record<ProductRoadmapHorizon, number> = { now: 0, next: 1, later: 2 };

function RoadmapListView({
  items,
  linkedItemLabels,
  onEdit,
  onDelete,
  onStatusChange,
  onAdd,
}: {
  items: ProductRoadmapItem[];
  linkedItemLabels: Record<string, string | null>;
  onEdit: (item: ProductRoadmapItem) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: ProductRoadmapItemStatus) => Promise<void>;
  onAdd: () => void;
}) {
  const sorted = [...items].sort((a, b) => {
    const hDiff = HORIZON_ORDER[a.horizon] - HORIZON_ORDER[b.horizon];
    return hDiff !== 0 ? hDiff : a.position - b.position;
  });

  if (sorted.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Table2 className="h-8 w-8 text-[var(--text-ter)] mb-3" />
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] font-medium">No items yet</p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 mb-4">
          Items you add to the board will appear here
        </p>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate)] hover:bg-[var(--slate)] text-white transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add item
        </button>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-separate border-spacing-y-1">
        <thead>
          <tr>
            <th className="text-left px-3 py-2 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide">
              Title
            </th>
            <th className="text-left px-3 py-2 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide w-24">
              Horizon
            </th>
            <th className="text-left px-3 py-2 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide w-36 hidden md:table-cell">
              Confidence
            </th>
            <th className="text-left px-3 py-2 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide w-36">
              Status
            </th>
            <th className="text-left px-3 py-2 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide hidden lg:table-cell">
              Linked item
            </th>
            <th className="w-16" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((item) => {
            const horizCfg = HORIZONS.find((h) => h.id === item.horizon)!;
            const confCfg = CONFIDENCE_LEVELS.find((c) => c.id === item.confidence)!;
            const linkedLabel = linkedItemLabels[item.id] ?? null;

            return (
              <tr
                key={item.id}
                className="group bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)]/50 transition-colors"
              >
                <td className="px-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => onEdit(item)}
                    className="text-left text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
                  >
                    {item.title || <span className="italic text-[var(--text-ter)]">Untitled</span>}
                  </button>
                  {item.description && (
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate max-w-xs mt-0.5">
                      {item.description}
                    </p>
                  )}
                  {item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {item.tags.map((t) => (
                        <span key={t} className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded bg-[var(--hover-tint)] text-[var(--text-ter)]">
                          <Tag className="h-2.5 w-2.5" />{t}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Confidence is drawn from md up and the linked item from lg up; below, they read here (audit #43). */}
                  <div className="lg:hidden flex flex-wrap items-center gap-1 mt-1">
                    <span className={`md:hidden inline-flex items-center text-xs px-1.5 py-0.5 rounded ${confCfg.badgeBg}`}>
                      {confCfg.label.split(' ')[0]}
                    </span>
                    {linkedLabel && (
                      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]/40">
                        <Link2 className="h-2.5 w-2.5" />
                        {linkedLabel}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${horizCfg.badgeBg}`}>
                    {horizCfg.label}
                  </span>
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell">
                  <span className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded ${confCfg.badgeBg}`}>
                    {confCfg.label.split(' ')[0]}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <ItemStatusBadge
                    status={item.status}
                    onChange={(s) => onStatusChange(item.id, s)}
                  />
                </td>
                <td className="px-3 py-2.5 hidden lg:table-cell">
                  {linkedLabel ? (
                    <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 text-[var(--text-sec)] border border-[var(--slate)] dark:border-[var(--slate)]/40">
                      <Link2 className="h-2.5 w-2.5" />
                      {linkedLabel}
                    </span>
                  ) : (
                    <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => onEdit(item)}
                      className="p-1.5 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                      title="Edit"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(item.id)}
                      className="p-1.5 rounded hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] border border-dashed border-[var(--rule)] transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> Add item
        </button>
      </div>
    </div>
  );
}

// ── Main editor ────────────────────────────────────────────

export default function ProductRoadmapEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    items,
    isLoading,
    error,
    fetchProductRoadmap,
    updateProductRoadmap,
    createItem,
    updateItem,
    deleteItem,
    moveItem,
    getDocumentById,
    getItemsForHorizon,
  } = useProductRoadmapStore();

  const workspaceItems = useItemsStore((s) => s.items);

  const [viewMode, setViewMode] = useState<ViewMode>('horizon');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [addingTo, setAddingTo] = useState<{ horizon: ProductRoadmapHorizon; confidence: ProductRoadmapConfidence } | null>(null);
  const [editingItem, setEditingItem] = useState<ProductRoadmapItem | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [dragItemId, setDragItemId] = useState<string | null>(null);
  const [dragTarget, setDragTarget] = useState<{ horizon: ProductRoadmapHorizon; confidence?: ProductRoadmapConfidence } | null>(null);

  useEffect(() => {
    if (id) fetchProductRoadmap(id);
  }, [id, fetchProductRoadmap]);

  const doc = id ? getDocumentById(id) : undefined;

  // Build linked item label map
  const linkedItemLabels: Record<string, string | null> = {};
  if (id) {
    items.filter((i) => i.roadmapId === id).forEach((item) => {
      if (item.itemId) {
        const wi = workspaceItems.find((w) => w.id === item.itemId);
        linkedItemLabels[item.id] = wi?.title ?? null;
      }
    });
  }

  const allRoadmapItems = id ? items.filter((i) => i.roadmapId === id) : [];

  const handleTitleSave = async () => {
    if (id && titleDraft.trim()) await updateProductRoadmap(id, { title: titleDraft.trim() });
    setEditingTitle(false);
  };

  const handleAddItem = async (updates: Partial<ProductRoadmapItem>) => {
    if (!id || !addingTo) return;
    await createItem(id, { ...updates, horizon: addingTo.horizon, confidence: addingTo.confidence });
    setAddingTo(null);
  };

  const handleEditItem = async (updates: Partial<ProductRoadmapItem>) => {
    if (!editingItem) return;
    await updateItem(editingItem.id, updates);
    setEditingItem(null);
  };

  const handleItemStatusChange = async (itemId: string, status: ProductRoadmapItemStatus) => {
    await updateItem(itemId, { status });
  };

  const handleDeleteItem = async (itemId: string) => {
    await deleteItem(itemId);
    setDeletingItemId(null);
  };

  // Drag — horizon view
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDragItemId(itemId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOverHorizon = (e: React.DragEvent, horizon: ProductRoadmapHorizon) => {
    e.preventDefault();
    setDragTarget({ horizon });
  };

  const handleDropHorizon = async (e: React.DragEvent, horizon: ProductRoadmapHorizon) => {
    e.preventDefault();
    setDragTarget(null);
    if (dragItemId) { await moveItem(dragItemId, horizon); setDragItemId(null); }
  };

  // Drag — uncertainties view (updates horizon AND confidence)
  const handleDragOverUncertainties = (e: React.DragEvent, horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => {
    e.preventDefault();
    setDragTarget({ horizon, confidence });
  };

  const handleDropUncertainties = async (e: React.DragEvent, horizon: ProductRoadmapHorizon, confidence: ProductRoadmapConfidence) => {
    e.preventDefault();
    setDragTarget(null);
    if (dragItemId) {
      await updateItem(dragItemId, { horizon, confidence });
      setDragItemId(null);
    }
  };

  if (isLoading && !doc) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--text-ter)]" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-12">
        {error && (
          <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 text-[var(--coral)] dark:text-[var(--coral)] text-sm mb-4">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
          </div>
        )}
        <p className="text-[var(--text-ter)] dark:text-[var(--text-sec)] text-center">Roadmap not found.</p>
        <Link to="/prioritization/product-roadmap" className="text-[var(--text-ter)] hover:underline text-sm mt-2 block text-center">
          ← Back to Product Roadmaps
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-4 md:px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate('/prioritization/product-roadmap')}
            className="p-1.5 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          {editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
              className="text-lg font-semibold bg-transparent border-b-2 border-[var(--slate)] outline-none text-[var(--text-pri)] dark:text-[var(--text-pri)] min-w-0 flex-1"
            />
          ) : (
            <button
              onClick={() => { setEditingTitle(true); setTitleDraft(doc.title); }}
              className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors text-left truncate"
            >
              {doc.title}
            </button>
          )}

          {/* View mode toggle */}
          {/* On a phone the status and the three views take their own row under the title */}
          <div className="ml-auto flex items-center gap-2 flex-shrink-0 max-md:ml-0 max-md:basis-full max-md:flex-wrap max-md:flex-shrink">
            {/* Document status — editable from the header */}
            <DocStatusBadge
              status={doc.status}
              onChange={(s) => updateProductRoadmap(doc.id, { status: s })}
            />

            <div className="flex items-center gap-1 p-1 bg-[var(--hover-tint)]">
              <button
                onClick={() => setViewMode('horizon')}
                className={`flex items-center gap-1.5 px-3 py-1.5  text-xs font-medium transition-colors ${
                  viewMode === 'horizon'
                    ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                    : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]'
                }`}
              >
                <LayoutList className="h-3.5 w-3.5" />
                Horizon
              </button>
              <button
                onClick={() => setViewMode('uncertainties')}
                className={`flex items-center gap-1.5 px-3 py-1.5  text-xs font-medium transition-colors ${
                  viewMode === 'uncertainties'
                    ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                    : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]'
                }`}
              >
                <Grid3x3 className="h-3.5 w-3.5" />
                Uncertainties
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5  text-xs font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] text-[var(--text-pri)] dark:text-[var(--text-pri)] shadow-sm'
                    : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]'
                }`}
              >
                <Table2 className="h-3.5 w-3.5" />
                List
              </button>
            </div>
          </div>
        </div>

        {doc.description && (
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1 ml-9">{doc.description}</p>
        )}
      </div>

      {/* Board / List */}
      <div className="flex-1 overflow-auto p-4 md:p-6">
        {viewMode === 'horizon' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-full">
            {HORIZONS.map((h) => (
              <HorizonColumn
                key={h.id}
                config={h}
                items={id ? getItemsForHorizon(id, h.id) : []}
                allHorizons={HORIZONS}
                linkedItemLabels={linkedItemLabels}
                onAdd={(horizon) => setAddingTo({ horizon, confidence: 'high' })}
                onEdit={(item) => setEditingItem(item)}
                onDelete={(itemId) => setDeletingItemId(itemId)}
                onMove={(itemId, horizon) => moveItem(itemId, horizon)}
                onDragOver={handleDragOverHorizon}
                onDrop={handleDropHorizon}
                onDragStart={handleDragStart}
                isDragTarget={dragTarget?.horizon === h.id && !dragTarget?.confidence}
              />
            ))}
          </div>
        )}

        {viewMode === 'uncertainties' && (
          <UncertaintiesView
            roadmapId={id!}
            allItems={allRoadmapItems}
            linkedItemLabels={linkedItemLabels}
            onAdd={(horizon, confidence) => setAddingTo({ horizon, confidence })}
            onEdit={(item) => setEditingItem(item)}
            onDelete={(itemId) => setDeletingItemId(itemId)}
            onMove={(itemId, horizon) => moveItem(itemId, horizon)}
            onDragStart={handleDragStart}
            dragTarget={dragTarget?.confidence ? (dragTarget as { horizon: ProductRoadmapHorizon; confidence: ProductRoadmapConfidence }) : null}
            onDragOver={handleDragOverUncertainties}
            onDrop={handleDropUncertainties}
          />
        )}

        {viewMode === 'list' && (
          <RoadmapListView
            items={allRoadmapItems}
            linkedItemLabels={linkedItemLabels}
            onEdit={(item) => setEditingItem(item)}
            onDelete={(itemId) => setDeletingItemId(itemId)}
            onStatusChange={handleItemStatusChange}
            onAdd={() => setAddingTo({ horizon: 'now', confidence: 'high' })}
          />
        )}
      </div>

      {/* Add item modal */}
      {addingTo && (
        <ItemEditModal
          item={{ horizon: addingTo.horizon, confidence: addingTo.confidence }}
          mode="create"
          onClose={() => setAddingTo(null)}
          onSave={handleAddItem}
          defaultConfidence={addingTo.confidence}
        />
      )}

      {/* Edit item modal */}
      {editingItem && (
        <ItemEditModal
          item={editingItem}
          mode="edit"
          onClose={() => setEditingItem(null)}
          onSave={handleEditItem}
        />
      )}

      {/* Delete confirm */}
      {deletingItemId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] shadow-2xl w-full max-w-sm mx-4 p-6">
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">Delete this card?</h2>
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-5">
              The card will be removed from the roadmap. Any linked workspace item will not be affected.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingItemId(null)} className="px-4 py-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors">
                Cancel
              </button>
              <button onClick={() => handleDeleteItem(deletingItemId)} className="px-4 py-2 text-sm bg-[var(--coral)] text-white font-medium hover:bg-[var(--coral)] transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
